import { useCallback, useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { tokenStorage } from '@/services/apiClient';
import { isSignedProxyUrl } from '@/hooks/useHlsPlayer';
import { createFileSink } from '@/lib/download/fileSink';
import { downloadHlsLevel, type DownloadResult } from '@/lib/download/downloadHlsLevel';

export type DownloadState =
  | { phase: 'idle' }
  | { phase: 'preparing' }
  | { phase: 'downloading'; pct: number }
  | { phase: 'done' }
  | { phase: 'error'; message: string }
  | { phase: 'canceled' };

export interface UseStreamDownloadOptions {
  /** Used to build the download filename. */
  title?: string;
  /** Chosen download rendition index; -1 (Auto) maps to the highest bitrate. */
  getDownloadLevel: () => number;
  // ---- injectable for tests ----
  fetchImpl?: typeof fetch;
  createSink?: typeof createFileSink;
  runDownload?: typeof downloadHlsLevel;
}

export interface UseStreamDownloadResult {
  state: DownloadState;
  start: () => Promise<void>;
  cancel: () => void;
}

/** Strip characters that are illegal in filenames across OSes. */
function sanitizeFilename(title: string | undefined): string {
  const base = (title ?? 'video').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
  return `${base || 'video'}.mp4`;
}

/** Index of the highest-bitrate rendition, for Auto downloads. */
function highestLevelIndex(hls: Hls): number {
  let best = 0;
  let bestBitrate = -1;
  hls.levels.forEach((lvl, i) => {
    if (lvl.bitrate > bestBitrate) { bestBitrate = lvl.bitrate; best = i; }
  });
  return best;
}

function variantUrl(hls: Hls, index: number): string | null {
  const lvl = hls.levels[index];
  if (!lvl) return null;
  const fromArray = Array.isArray(lvl.url) ? lvl.url[0] : undefined;
  return fromArray ?? (lvl as unknown as { uri?: string }).uri ?? null;
}

/**
 * A token-aware fetch mirroring useHlsPlayer's xhrSetup: attach the Bearer
 * token to non-signed routes (the master/variant playlist), skip it on signed
 * proxy segment routes (which auth via the URL HMAC and must stay "simple" GETs
 * to avoid a CORS preflight per segment).
 */
function tokenAwareFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === 'string' ? input : input.toString();
  const headers = new Headers(init?.headers);
  if (!isSignedProxyUrl(url)) {
    const token = tokenStorage.get();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}

/**
 * Drives a client-side "download this VOD as .mp4" flow off the live hls.js
 * instance. Resolves the target variant playlist for the chosen quality (or the
 * highest rendition when Auto), opens a file sink, and runs `downloadHlsLevel`
 * under an AbortController. State machine: idle → preparing → downloading(pct)
 * → done | error | canceled.
 */
export function useStreamDownload(
  hls: Hls | null,
  options: UseStreamDownloadOptions,
): UseStreamDownloadResult {
  const [state, setState] = useState<DownloadState>({ phase: 'idle' });
  const controllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  // Keep the latest options without re-creating `start` each render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => () => { mountedRef.current = false; controllerRef.current?.abort(); }, []);

  const safeSet = useCallback((next: DownloadState): void => {
    if (mountedRef.current) setState(next);
  }, []);

  const start = useCallback(async (): Promise<void> => {
    const opts = optionsRef.current;
    const createSink = opts.createSink ?? createFileSink;
    const runDownload = opts.runDownload ?? downloadHlsLevel;
    const fetchImpl = opts.fetchImpl ?? tokenAwareFetch;

    if (!hls) { safeSet({ phase: 'error', message: 'No stream to download' }); return; }

    safeSet({ phase: 'preparing' });

    const chosen = opts.getDownloadLevel();
    const levelIndex = chosen === -1 ? highestLevelIndex(hls) : chosen;
    const playlistUrl = variantUrl(hls, levelIndex);
    if (!playlistUrl) { safeSet({ phase: 'error', message: 'Quality unavailable' }); return; }

    const controller = new AbortController();
    controllerRef.current = controller;

    let result: DownloadResult;
    try {
      const sink = await createSink(sanitizeFilename(opts.title), 'video/mp4');
      safeSet({ phase: 'downloading', pct: 0 });
      result = await runDownload({
        playlistUrl,
        fetchImpl,
        sink,
        signal: controller.signal,
        onProgress: (done, total) => {
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          safeSet({ phase: 'downloading', pct });
        },
      });
    } catch (err) {
      // User dismissed the file-save dialog → a cancel, not a failure.
      if (err instanceof Error && err.name === 'AbortError') { safeSet({ phase: 'canceled' }); return; }
      safeSet({ phase: 'error', message: err instanceof Error ? err.message : 'Download failed' });
      return;
    }

    if (result.canceled) safeSet({ phase: 'canceled' });
    else if (result.ok) safeSet({ phase: 'done' });
    else safeSet({ phase: 'error', message: result.error ?? 'Download failed' });
  }, [hls, safeSet]);

  const cancel = useCallback((): void => {
    controllerRef.current?.abort();
  }, []);

  return { state, start, cancel };
}
